import React, { useEffect, useMemo, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { ToastContainer, toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import {
  Layers,
  Building,
  Search,
  Plus,
  MapPin,
  ShieldCheck,
  CheckCircle2,
  Trash2,
  Edit2,
  Sliders,
  MoreVertical,
  Users,
  Upload,
} from "lucide-react";
import { FaTimes } from "react-icons/fa";
import { useSite } from "../store/useSite";
import { getDashboardContextValue } from "../utils/dashboardContext";
import { useAuth } from "../store/useAuth";
import { AddHallModal } from "../components/AddHallModal";
import { AddZoneModal } from "../components/AddZoneModal";
import { CustomSelect } from "../components/common/CustomSelect";
import {
  getHalls,
  createHall as createHallApi,
  updateHall as updateHallApi,
  deleteHall as deleteHallApi,
  updateZone,
  createZone,
  deleteZone,
  getAllZones,
} from "../services/zone";
import { useTimezoneStore } from "../store/useTimezoneStore";
import {
  formatDisplayDateTime,
  getTimezoneAbbr,
} from "../utils/timezoneFormatter";
import { ZoneAssigneeModal } from "../components/sessions/SessionAssigneeModal";
import BulkAssigneeImportModal from "../components/sessions/BulkAssigneeImportModal";

const ZONE_MODAL_TOAST_ID = "zone-modal-toast";
const zoneModalToastOptions = {
  containerId: ZONE_MODAL_TOAST_ID,
};

const fmtDT = (iso, tz) => {
  if (!iso) return "";
  const targetTz = tz || useTimezoneStore.getState().getActiveTimezone();
  return formatDisplayDateTime(iso, targetTz);
};

function ConfirmDelete({ open, onClose, onConfirm, title, message }) {
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-2xl overflow-hidden border border-gray-100">
        <div className="px-5 pt-5 pb-2 text-lg font-bold text-gray-900">{title}</div>
        <div className="px-5 pb-5 pt-1 text-sm text-gray-600">{message}</div>
        <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3 border-t border-gray-100 px-5 py-4 bg-gray-50/50">
          <button
            type="button"
            className="h-9 rounded-md border border-gray-300 px-4 text-xs font-semibold uppercase tracking-wide text-gray-700 hover:bg-white"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="h-9 rounded-md bg-red-600 px-4 text-xs font-semibold uppercase tracking-wide text-white hover:bg-red-700 disabled:opacity-60 shadow-xs"
            onClick={async () => {
              try {
                setBusy(true);
                await onConfirm();
                onClose();
              } finally {
                setBusy(false);
              }
            }}
            disabled={busy}
          >
            {busy ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function normalizeZone(api) {
  if (!api) return null;
  const access =
    Array.isArray(api.access)
      ? api.access
      : (() => {
        try {
          const p = typeof api.access === "string" ? JSON.parse(api.access) : api.access;
          return Array.isArray(p) ? p : [];
        } catch {
          return [];
        }
      })();

  return {
    id: api.id ?? api.zone_id ?? api._id ?? api.Id,
    event_id: api.event_id,
    name: api.name ?? "",
    location: api.location ?? api.hall_name ?? "",
    hall_id: api.hall_id ?? null,
    opens_at: api.opens_at_utc ?? api.opens_at ?? api.opensAt ?? api.opens_at_ist ?? null,
    closes_at: api.closes_at_utc ?? api.closes_at ?? api.closesAt ?? api.closes_at_ist ?? null,
    opens_at_utc: api.opens_at_utc ?? api.opens_at ?? api.opensAt ?? null,
    closes_at_utc: api.closes_at_utc ?? api.closes_at ?? api.closesAt ?? null,
    access,
    audience_type: api.audience_type ?? api.audienceType ?? "registrations",
    audience_config: api.audience_config ?? api.audienceConfig ?? null,
    multiple_entry: Number(api.multiple_entry) === 1 ? 1 : 0,
    required_check_out: Number(api.required_check_out) === 1 ? 1 : 0,
  };
}

function formatAccessItem(item) {
  if (!item || typeof item !== "object") return "";
  const roleLabel =
    item.role === "attendee"
      ? "Attendees"
      : item.role === "speaker"
        ? "Speakers"
        : item.role === "sponsor"
          ? "Sponsors"
          : item.role === "exhibitor"
            ? "Exhibitors"
            : String(item.role || "");

  if (item.scope === "all") return `${roleLabel} (all)`;

  if (item.scope === "selected") {
    const count = Array.isArray(item.ticket_class_ids)
      ? item.ticket_class_ids.length
      : Array.isArray(item.sponsor_ids)
        ? item.sponsor_ids.length
        : Array.isArray(item.exhibitor_ticket_class_ids)
          ? item.exhibitor_ticket_class_ids.length
          : 0;
    return `${roleLabel} (${count} selected)`;
  }

  if (item.scope === "selected_registrations" || Array.isArray(item.registration_ids)) {
    return `Registrations (${item.registration_ids?.length || 0} selected)`;
  }

  if (item.role === "all") return "Open to All";
  return roleLabel;
}

function formatAccessSummary(accessArray) {
  if (!Array.isArray(accessArray)) return [];
  return accessArray.map(formatAccessItem).filter(Boolean);
}

function ZonesPage() {
  const { eventData } = useSite();
  const eventTz = eventData?.timezone || "Asia/Kolkata";
  const eventTzAbbr = getTimezoneAbbr(eventTz);
  const { getActiveTimezone } = useTimezoneStore();
  const activeTz = getActiveTimezone(eventTz);

  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "halls" ? "halls" : "zones";
  const [activeTab, setActiveTab] = useState(initialTab);

  const [zones, setZones] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [locationFilter, setLocationFilter] = useState("All");
  const [loading, setLoading] = useState(false);

  const [halls, setHalls] = useState([]);
  const [hallsLoading, setHallsLoading] = useState(false);
  const [hallSearch, setHallSearch] = useState("");
  const [hallModalOpen, setHallModalOpen] = useState(false);
  const [editingHall, setEditingHall] = useState(null);
  const [toDeleteHall, setToDeleteHall] = useState(null);
  const [delHallOpen, setDelHallOpen] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [zoneSettingsModal, setZoneSettingsModal] = useState(null);
  const [assigneeTarget, setAssigneeTarget] = useState(null);

  const [delOpen, setDelOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  const [activeMenu, setActiveMenu] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const authUser = useAuth((state) => state.user);
  const authData = useAuth((state) => state.authData);
  const authEventId = useAuth((state) => state.eventId);
  const contextEventId = getDashboardContextValue("eventId");
  const eventId = authEventId || contextEventId || eventData?.id || "";
  const token = authUser?.token || authData?.token || useAuth.getState().user?.token || "";

  const toggleZoneMenu = useCallback((e, zone) => {
    e.stopPropagation();
    if (activeMenu?.id === zone.id) {
      setActiveMenu(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 176;
    const menuHeight = 170;
    const gutter = 8;
    const preferredTop = rect.bottom + 4;
    const top =
      preferredTop + menuHeight > window.innerHeight - gutter
        ? Math.max(gutter, rect.top - menuHeight - 4)
        : preferredTop;
    const left = Math.max(
      gutter,
      Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - gutter)
    );
    setMenuPos({ top, left });
    setActiveMenu(zone);
  }, [activeMenu]);

  useEffect(() => {
    if (!activeMenu) return;
    const handleClose = () => setActiveMenu(null);
    window.addEventListener("scroll", handleClose, true);
    window.addEventListener("resize", handleClose);
    return () => {
      window.removeEventListener("scroll", handleClose, true);
      window.removeEventListener("resize", handleClose);
    };
  }, [activeMenu]);

  const [allLocations, setAllLocations] = useState([]);

  const loadZones = async (searchTerm = search) => {
    if (!eventId) return;
    try {
      setLoading(true);
      const res = await getAllZones(eventId, token, searchTerm);
      const data = res.data?.data?.zones ?? res.data?.zones ?? res.data?.data ?? [];
      const norm = (Array.isArray(data) ? data : []).map(normalizeZone).filter(Boolean);
      setZones(norm);
      if (norm.length > 0) {
        const locs = norm.map((z) => z.location).filter(Boolean);
        setAllLocations((prev) => Array.from(new Set([...prev, ...locs])));
      }
    } catch (e) {
      toast.error("Could not load zones", zoneModalToastOptions);
    } finally {
      setLoading(false);
    }
  };

  const fetchHallsList = async (searchTerm = hallSearch) => {
    if (!eventId) return;
    try {
      setHallsLoading(true);
      const res = await getHalls(eventId, token, searchTerm);
      const rows = res.data?.halls || res.data?.data?.halls || res.data?.data || res.data || [];
      const arr = Array.isArray(rows) ? rows : [];
      setHalls(arr);
      if (arr.length > 0) {
        const locs = arr.map((h) => h.name).filter(Boolean);
        setAllLocations((prev) => Array.from(new Set([...prev, ...locs])));
      }
    } catch (e) {
      toast.error("Failed to load halls", zoneModalToastOptions);
    } finally {
      setHallsLoading(false);
    }
  };

  useEffect(() => {
    if (!eventId) return;
    const timer = setTimeout(() => {
      loadZones(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [eventId, search]);

  useEffect(() => {
    if (!eventId) return;
    const timer = setTimeout(() => {
      fetchHallsList(hallSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [eventId, hallSearch]);

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setSearchParams({ tab: tabKey });
  };

  useEffect(() => {
    if (locationFilter === "All") {
      setFiltered(zones);
    } else {
      setFiltered(zones.filter((z) => (z?.location || "").toLowerCase() === locationFilter.toLowerCase()));
    }
  }, [zones, locationFilter]);

  const filteredHalls = halls;

  const locationOptions = useMemo(() => {
    const uniqueLocations = Array.from(
      new Set([...allLocations, ...zones.map((z) => z.location).filter(Boolean)])
    );
    return [
      { value: "All", label: "All Locations" },
      ...uniqueLocations.map((loc) => ({ value: loc, label: loc })),
    ];
  }, [allLocations, zones]);

  const createHall = async ({ name, address }) => {
    const res = await createHallApi({ name, address, event_id: eventId }, token);
    const newId = res.data?.data?.id || res.data?.id || Math.random().toString(36).slice(2);
    const data = {
      id: newId,
      name,
      address,
    };
    await fetchHallsList();
    return data;
  };

  const handleSaveHall = async ({ id, name, address }) => {
    try {
      if (id) {
        await updateHallApi(id, { name, address }, token);
        setHalls((prev) =>
          prev.map((h) => (String(h.id) === String(id) ? { ...h, name, address } : h))
        );
        setZones((prev) =>
          prev.map((z) =>
            String(z.hall_id) === String(id) ? { ...z, location: name } : z
          )
        );
        toast.success("Hall updated successfully");
      } else {
        const res = await createHallApi({ name, address, event_id: eventId }, token);
        const newId = res.data?.data?.id || res.data?.id || Math.random().toString(36).slice(2);
        const newHall = {
          id: newId,
          name,
          address,
        };
        setHalls((prev) => [newHall, ...prev]);
        await fetchHallsList();
        toast.success("Hall created successfully");
        return newHall;
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || (id ? "Failed to update hall" : "Failed to create hall"));
      throw err;
    }
  };

  const doDeleteHall = async () => {
    if (!toDeleteHall) return;
    try {
      await deleteHallApi(toDeleteHall.id, token);
      setHalls((prev) => prev.filter((h) => String(h.id) !== String(toDeleteHall.id)));
      setZones((prev) =>
        prev.map((z) =>
          String(z.hall_id) === String(toDeleteHall.id) ? { ...z, location: "" } : z
        )
      );
      toast.success("Hall deleted successfully");
    } catch {
      toast.error("Failed to delete hall");
    }
  };

  const openAdd = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (z) => {
    setEditing({
      id: z.id,
      name: z.name,
      location: z.location,
      hall_id: z.hall_id,
      opensAt: z.opens_at_utc || z.opens_at,
      closesAt: z.closes_at_utc || z.closes_at,
      access: z.access,
      audience_type: z.audience_type,
      audience_config: z.audience_config,
      multiple_entry: z.multiple_entry,
      required_check_out: z.required_check_out,
    });
    setModalOpen(true);
  };

  const openZoneSettings = (z) => {
    setActiveMenu(null);
    setZoneSettingsModal({
      id: z.id,
      title: z.name,
      subtitle: `${z.location || "No Hall"} • Opens ${fmtDT(z.opens_at, activeTz)}`,
      multipleEntry: Number(z.multiple_entry) === 1,
      requiredCheckOut: Number(z.required_check_out) === 1,
      zone: z,
    });
  };

  const saveZoneSettings = async () => {
    if (!zoneSettingsModal) return;
    const targetZone = zoneSettingsModal.zone;
    try {
      const payload = {
        id: targetZone.id,
        name: targetZone.name,
        hall_id: targetZone.hall_id,
        opensAt: targetZone.opens_at_utc || targetZone.opens_at,
        closesAt: targetZone.closes_at_utc || targetZone.closes_at,
        access: targetZone.access,
        audience_type: targetZone.audience_type || "registrations",
        audience_config: targetZone.audience_config || null,
        multiple_entry: zoneSettingsModal.multipleEntry ? 1 : 0,
        required_check_out:
          zoneSettingsModal.requiredCheckOut && zoneSettingsModal.multipleEntry ? 1 : 0,
      };
      await updateZone(targetZone.id, eventId, payload, token);
      setZones((prev) =>
        prev.map((item) =>
          item.id === targetZone.id
            ? {
                ...item,
                multiple_entry: payload.multiple_entry,
                required_check_out: payload.required_check_out,
              }
            : item
        )
      );
      setZoneSettingsModal(null);
      toast.success("Zone settings updated");
    } catch {
      toast.error("Failed to update zone settings");
    }
  };

  const openAssignee = (z) => {
    setActiveMenu(null);
    setAssigneeTarget({
      id: z.id,
      title: z.name,
      subtitle: `${z.location || "No Hall"} • Opens ${fmtDT(z.opens_at, activeTz)}`,
      audience_type: z.audience_type || "registrations",
      audience_config: z.audience_config || null,
      access: z.access || [],
      zone: z,
    });
  };

  const handleSaveZoneAssignee = async ({ audienceType, audienceConfig, access }) => {
    if (!assigneeTarget) return;
    const targetZone = assigneeTarget.zone;
    const payload = {
      id: targetZone.id,
      name: targetZone.name,
      hall_id: targetZone.hall_id,
      opensAt: targetZone.opens_at_utc || targetZone.opens_at,
      closesAt: targetZone.closes_at_utc || targetZone.closes_at,
      access,
      audience_type: audienceType,
      audience_config: audienceConfig,
      multiple_entry: targetZone.multiple_entry ?? 0,
      required_check_out: targetZone.required_check_out ?? 0,
    };
    await updateZone(targetZone.id, eventId, payload, token);
    setZones((prev) =>
      prev.map((item) =>
        item.id === targetZone.id
          ? {
              ...item,
              access,
              audience_type: audienceType,
              audience_config: audienceConfig,
            }
          : item
      )
    );
    setAssigneeTarget(null);
  };

  const handleBulkImportZoneAssignees = async (payload) => {
    if (!zones || zones.length === 0) throw new Error("No zones available");

    const entries = Object.entries(payload);
    for (const [zoneKey, regIds] of entries) {
      const targetZone = zones.find(
        (z) =>
          String(z.id) === String(zoneKey) ||
          String(z.name || "").toLowerCase() === String(zoneKey).toLowerCase()
      );
      if (!targetZone) continue;

      const existingSelected = Array.isArray(
        targetZone.audience_config?.selectedRegistrationIds
      )
        ? targetZone.audience_config.selectedRegistrationIds
        : [];
      const mergedSelected = Array.from(
        new Set([...existingSelected.map(String), ...regIds.map(String)])
      );

      const updatedAudienceConfig = {
        ...(targetZone.audience_config || {}),
        category: "registrations",
        openToAll: false,
        registrationMode: "selected",
        selectedRegistrationIds: mergedSelected,
        allRegistrationsExcluded: false,
        excludedRegistrationIds: [],
        importedSubmissionIds: [],
        importedRegistrations: [],
        importedFileName: "",
        submission_ids: [],
      };

      const existingTicketClassIds = Array.isArray(
        targetZone.audience_config?.ticketClassIds
      )
        ? targetZone.audience_config.ticketClassIds
        : [];

      const updatedAccess = [
        {
          role: "attendee",
          scope: "selected",
          ticket_class_ids: existingTicketClassIds
            .map(Number)
            .filter(Boolean),
          registration_ids: mergedSelected,
        },
      ];

      const updatePayload = {
        name: targetZone.name,
        hall_id: targetZone.hall_id,
        opensAt: targetZone.opens_at_utc || targetZone.opens_at,
        closesAt: targetZone.closes_at_utc || targetZone.closes_at,
        access: updatedAccess,
        audience_type: "registrations",
        audience_config: updatedAudienceConfig,
        multiple_entry: targetZone.multiple_entry ?? 0,
        required_check_out: targetZone.required_check_out ?? 0,
      };

      await updateZone(targetZone.id, eventId, updatePayload, token);
    }

    setZones((prev) =>
      prev.map((item) => {
        const matchedEntry = entries.find(
          ([key]) =>
            String(key) === String(item.id) ||
            String(key).toLowerCase() === String(item.name || "").toLowerCase()
        );
        if (!matchedEntry) return item;
        const newRegIds = matchedEntry[1];
        const existingSelected = Array.isArray(
          item.audience_config?.selectedRegistrationIds
        )
          ? item.audience_config.selectedRegistrationIds
          : [];
        const mergedSelected = Array.from(
          new Set([...existingSelected.map(String), ...newRegIds.map(String)])
        );
        return {
          ...item,
          audience_type: "registrations",
          audience_config: {
            ...(item.audience_config || {}),
            category: "registrations",
            openToAll: false,
            registrationMode: "selected",
            selectedRegistrationIds: mergedSelected,
            allRegistrationsExcluded: false,
            excludedRegistrationIds: [],
          },
          access: [
            {
              role: "attendee",
              scope: "selected",
              ticket_class_ids: (item.audience_config?.ticketClassIds || [])
                .map(Number)
                .filter(Boolean),
              registration_ids: mergedSelected,
            },
          ],
        };
      })
    );
  };

  const saveZone = async (payload) => {
    try {
      if (payload.id) {
        const res = await updateZone(
          payload.id,
          eventId,
          {
            name: payload.name,
            hall_id: payload.hall_id,
            opensAt: payload.opensAt,
            closesAt: payload.closesAt,
            access: payload.access,
            audience_type: payload.audience_type ?? "registrations",
            audience_config: payload.audience_config ?? null,
            multiple_entry: payload.multiple_entry ?? 0,
            required_check_out: payload.required_check_out ?? 0,
          },
          token,
        );

        const returned =
          res.data?.data?.zone ?? res.data?.zone ?? res.data?.data ?? null;

        const updated = normalizeZone(returned) ?? {
          ...payload,
          opens_at: payload.opensAt,
          closes_at: payload.closesAt,
          multiple_entry: payload.multiple_entry ?? 0,
          required_check_out: payload.required_check_out ?? 0,
        };

        setZones((prev) => prev.map((z) => (z.id === payload.id ? { ...z, ...updated } : z)));
        toast.success("Zone updated");
      } else {
        const res = await createZone(
          eventId,
          {
            name: payload.name,
            hall_id: payload.hall_id,
            opensAt: payload.opensAt,
            closesAt: payload.closesAt,
            access: payload.access,
            audience_type: payload.audience_type ?? "registrations",
            audience_config: payload.audience_config ?? null,
            multiple_entry: payload.multiple_entry ?? 0,
            required_check_out: payload.required_check_out ?? 0,
          },
          token,
        );

        const created =
          res.data?.data?.zone ??
          res.data?.zone ??
          res.data?.data ??
          null;

        const newZone = normalizeZone(created) ?? {
          ...payload,
          id: Math.random().toString(36).slice(2),
          opens_at: payload.opensAt,
          closes_at: payload.closesAt,
          multiple_entry: payload.multiple_entry ?? 0,
          required_check_out: payload.required_check_out ?? 0,
        };

        setZones((prev) => [newZone, ...prev]);
        toast.success("Zone added");
      }
    } catch (e) {
      toast.error("Could not save zone", zoneModalToastOptions);
      throw e;
    }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteZone(toDelete.id, eventId, token);
      setZones((prev) => prev.filter((x) => x.id !== toDelete.id));
      toast.success("Zone deleted");
    } catch {
      toast.error("Could not delete zone");
    }
  };

  if (assigneeTarget) {
    return (
      <>
        <ZoneAssigneeModal
          zoneTarget={assigneeTarget}
          eventId={eventId}
          token={token}
          eventTz={eventTz}
          onClose={() => setAssigneeTarget(null)}
          onSave={handleSaveZoneAssignee}
        />
        <ToastContainer position="top-right" autoClose={3000} />
      </>
    );
  }

  const zonesCount = zones.length;
  const multipleEntryZonesCount = zones.filter((z) => Number(z.multiple_entry) === 1).length;
  const requiredCheckoutZonesCount = zones.filter((z) => Number(z.required_check_out) === 1).length;
  const activeLocationsCount = new Set(zones.map((z) => z.location).filter(Boolean)).size;

  const hallsCount = halls.length;
  const hallsAssignedCount = halls.filter((h) =>
    zones.some(
      (z) =>
        String(z.hall_id) === String(h.id) ||
        (z.location && z.location.toLowerCase() === (h.name || "").toLowerCase())
    )
  ).length;
  const hallsUnassignedCount = hallsCount - hallsAssignedCount;

  return (
    <div className="min-h-screen bg-white text-[#1b1b23]">
      <ToastContainer position="top-right" autoClose={3000} />
      <header className="bg-white border-b border-gray-100">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8 pt-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-[#1b1b23]">
                Zones
              </h1>
              <p className="text-[#464554] text-sm mt-0.5">
                Manage access zones, venue halls and entry restrictions
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setBulkImportOpen(true)}
                className="h-9 px-3 text-sm font-medium text-gray-600 hover:text-gray-900 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg flex items-center gap-1.5 transition-all shadow-xs"
              >
                <Upload className="w-4 h-4 text-gray-600" />
                <span className="inline-block">Import Assignees</span>
              </button>
            </div>
          </div>

          <div className="mt-4 border-b border-gray-200">
            <div className="flex gap-6 overflow-x-auto">
              <button
                type="button"
                onClick={() => handleTabChange("zones")}
                className={`px-2 py-2 text-sm font-medium transition-colors ${
                  activeTab === "zones"
                    ? "text-blue-700 border-b-2 border-blue-600 font-bold"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Zones
              </button>

              <button
                type="button"
                onClick={() => handleTabChange("halls")}
                className={`px-2 py-2 text-sm font-medium transition-colors ${
                  activeTab === "halls"
                    ? "text-blue-700 border-b-2 border-blue-600 font-bold"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Halls
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 md:px-8 py-6 space-y-6">
        {activeTab === "zones" ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-1 shadow-xs">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Total Zones
                  </span>
                  <span className="p-2 rounded-lg bg-indigo-50 text-indigo-600">
                    <Layers className="w-4 h-4" />
                  </span>
                </div>
                <p className="text-2xl font-extrabold text-gray-900">{zonesCount}</p>
                <p className="text-[11px] text-gray-400">Configured event areas</p>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-1 shadow-xs">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Multiple Entry
                  </span>
                  <span className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
                    <CheckCircle2 className="w-4 h-4" />
                  </span>
                </div>
                <p className="text-2xl font-extrabold text-gray-900">{multipleEntryZonesCount}</p>
                <p className="text-[11px] text-gray-400">Re-entry allowed</p>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-1 shadow-xs">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Required Checkout
                  </span>
                  <span className="p-2 rounded-lg bg-purple-50 text-purple-600">
                    <ShieldCheck className="w-4 h-4" />
                  </span>
                </div>
                <p className="text-2xl font-extrabold text-gray-900">{requiredCheckoutZonesCount}</p>
                <p className="text-[11px] text-gray-400">Controlled exit flow</p>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-1 shadow-xs">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Locations
                  </span>
                  <span className="p-2 rounded-lg bg-amber-50 text-amber-600">
                    <MapPin className="w-4 h-4" />
                  </span>
                </div>
                <p className="text-2xl font-extrabold text-gray-900">{activeLocationsCount}</p>
                <p className="text-[11px] text-gray-400">Assigned hall venues</p>
              </div>
            </div>

            <div className="bg-white rounded-md border border-gray-200 p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-xs">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-1">
                <div className="relative min-w-full sm:min-w-[280px]">
                  <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full text-sm border border-gray-200 rounded-md pl-9 pr-3 py-2 bg-white text-[#1b1b23] focus:outline-none focus:ring-1 focus:ring-[#152736] focus:border-[#152736]"
                    placeholder="Search zones by name or hall..."
                  />
                </div>

                <div className="w-full sm:w-[220px]">
                  <CustomSelect
                    options={locationOptions}
                    value={locationFilter}
                    onChange={setLocationFilter}
                    placeholder="All Locations"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={openAdd}
                  className="flex items-center justify-center gap-2 px-4 py-2 rounded-md text-xs font-semibold uppercase tracking-wide transition-all duration-150 bg-[#123047] text-white hover:bg-[#0b2235] shadow-xs shrink-0"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add New Zone</span>
                </button>
              </div>
            </div>

            <div className="bg-white rounded-md border border-gray-200 overflow-hidden shadow-xs">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100 text-left">
                    <tr>
                      <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-[#464554]">
                        Zone Name
                      </th>
                      <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-[#464554]">
                        Location / Hall
                      </th>
                      <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-[#464554]">
                        Access Time
                      </th>
                      <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-[#464554]">
                        Access Scope
                      </th>
                      <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-[#464554] text-right">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-12 text-center text-gray-500">
                          <div className="flex flex-col items-center justify-center gap-2">
                            <Layers className="w-8 h-8 text-gray-300" />
                            <p className="text-sm font-medium text-gray-600">No zones found</p>
                            <p className="text-xs text-gray-400">
                              {search || locationFilter !== "All"
                                ? "Try adjusting your search or filters"
                                : "Get started by creating your first zone"}
                            </p>
                            {!search && locationFilter === "All" && (
                              <button
                                type="button"
                                onClick={openAdd}
                                className="mt-2 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md bg-[#123047] text-white text-xs font-semibold uppercase tracking-wide hover:bg-[#0b2235]"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                <span>Create Zone</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filtered.map((z) => {
                        const labels = formatAccessSummary(z.access);

                        return (
                          <tr key={z.id} className="hover:bg-gray-50/70 transition-colors">
                            <td className="py-3.5 px-4">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900">{z.name}</span>
                                <span
                                  className="bg-gray-100 hover:bg-gray-200 text-gray-600 text-[10px] px-1.5 py-0.5 rounded font-mono font-normal cursor-pointer select-all transition-colors inline-flex items-center"
                                  title="Click to copy Zone ID for Excel Import"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(z.id);
                                    toast.success("Copied Zone ID to clipboard!");
                                  }}
                                >
                                  ID: {z.id}
                                </span>
                              </div>
                            </td>

                            <td className="py-3.5 px-4">
                              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-50 text-gray-700 text-xs font-medium border border-gray-100">
                                <MapPin className="w-3.5 h-3.5 text-gray-400" />
                                <span>{z.location || "Unassigned"}</span>
                              </div>
                            </td>

                            <td className="py-3.5 px-4">
                              <div className="text-xs text-gray-600 space-y-0.5">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[10px] uppercase font-bold text-gray-400 w-11">Open:</span>
                                  <span>{fmtDT(z.opens_at, activeTz) || "—"}</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[10px] uppercase font-bold text-gray-400 w-11">Close:</span>
                                  <span>{fmtDT(z.closes_at, activeTz) || "—"}</span>
                                </div>
                              </div>
                            </td>

                            <td className="py-3.5 px-4">
                              <div className="flex flex-wrap items-center gap-1.5 max-w-[260px]">
                                {labels.length === 0 ? (
                                  <span className="text-xs text-gray-400">Open to All</span>
                                ) : (
                                  <>
                                    {labels.slice(0, 2).map((text, idx) => (
                                      <span
                                        key={idx}
                                        className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-700 font-medium"
                                      >
                                        {text}
                                      </span>
                                    ))}
                                    {labels.length > 2 && (
                                      <span className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-600">
                                        +{labels.length - 2}
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>

                            <td className="py-3.5 px-4 text-right">
                              <button
                                type="button"
                                onClick={(e) => toggleZoneMenu(e, z)}
                                className="text-gray-500 hover:text-gray-800 transition-colors p-1 cursor-pointer inline-flex items-center justify-center"
                              >
                                <MoreVertical className="w-5 h-5 text-gray-600 hover:text-blue-600" />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-1 shadow-xs">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Total Halls
                  </span>
                  <span className="p-2 rounded-lg bg-blue-50 text-blue-600">
                    <Building className="w-4 h-4" />
                  </span>
                </div>
                <p className="text-2xl font-extrabold text-gray-900">{hallsCount}</p>
                <p className="text-[11px] text-gray-400">Created venue halls</p>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-1 shadow-xs">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Assigned to Zones
                  </span>
                  <span className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
                    <Layers className="w-4 h-4" />
                  </span>
                </div>
                <p className="text-2xl font-extrabold text-gray-900">{hallsAssignedCount}</p>
                <p className="text-[11px] text-gray-400">Active in one or more zones</p>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-1 shadow-xs">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Unassigned Halls
                  </span>
                  <span className="p-2 rounded-lg bg-gray-50 text-gray-600">
                    <MapPin className="w-4 h-4" />
                  </span>
                </div>
                <p className="text-2xl font-extrabold text-gray-900">{hallsUnassignedCount}</p>
                <p className="text-[11px] text-gray-400">Ready for zone assignment</p>
              </div>
            </div>

            <div className="bg-white rounded-md border border-gray-200 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-xs">
              <div className="relative min-w-full sm:min-w-[320px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  value={hallSearch}
                  onChange={(e) => setHallSearch(e.target.value)}
                  className="w-full text-sm border border-gray-200 rounded-md pl-9 pr-3 py-2 bg-white text-[#1b1b23] focus:outline-none focus:ring-1 focus:ring-[#152736] focus:border-[#152736]"
                  placeholder="Search halls by name or address..."
                />
              </div>

              <button
                type="button"
                onClick={() => {
                  setEditingHall(null);
                  setHallModalOpen(true);
                }}
                className="flex items-center justify-center gap-2 px-4 py-2 rounded-md text-xs font-semibold uppercase tracking-wide transition-all duration-150 bg-[#123047] text-white hover:bg-[#0b2235] shadow-xs shrink-0"
              >
                <Plus className="w-4 h-4" />
                <span>Add Hall</span>
              </button>
            </div>

            <div className="bg-white rounded-md border border-gray-200 overflow-hidden shadow-xs">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100 text-left">
                    <tr>
                      <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-[#464554] w-12">
                        #
                      </th>
                      <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-[#464554]">
                        Hall Name
                      </th>
                      <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-[#464554]">
                        Address / Details
                      </th>
                      <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-[#464554] text-right">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredHalls.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="py-12 text-center text-gray-500">
                          <div className="flex flex-col items-center justify-center gap-2">
                            <Building className="w-8 h-8 text-gray-300" />
                            <p className="text-sm font-medium text-gray-600">No halls found</p>
                            <p className="text-xs text-gray-400">
                              {hallSearch
                                ? "No halls match your search query"
                                : "Halls created in the Add Zone popup will appear here"}
                            </p>
                            {!hallSearch && (
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingHall(null);
                                  setHallModalOpen(true);
                                }}
                                className="mt-2 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md bg-[#123047] text-white text-xs font-semibold uppercase tracking-wide hover:bg-[#0b2235]"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                <span>Create First Hall</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredHalls.map((hall, index) => {
                        return (
                          <tr key={hall.id || index} className="hover:bg-gray-50/70 transition-colors">
                            <td className="py-3.5 px-4 text-xs text-gray-400 font-mono">
                              {index + 1}
                            </td>

                            <td className="py-3.5 px-4">
                              <div className="flex items-center gap-2.5">
                                <div className="p-2 rounded-lg bg-blue-50 text-blue-600 shrink-0">
                                  <Building className="w-4 h-4" />
                                </div>
                                <div className="font-semibold text-gray-900">{hall.name}</div>
                              </div>
                            </td>

                            <td className="py-3.5 px-4">
                              <div className="text-sm text-gray-600 max-w-sm truncate">
                                {hall.address || "—"}
                              </div>
                            </td>

                            <td className="py-3.5 px-4 text-right">
                              <div className="inline-flex items-center gap-2 justify-end">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingHall(hall);
                                    setHallModalOpen(true);
                                  }}
                                  className="h-8 px-3 rounded-md border border-gray-200 text-xs font-semibold uppercase tracking-wider text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setToDeleteHall(hall);
                                    setDelHallOpen(true);
                                  }}
                                  className="h-8 px-3 rounded-md border border-gray-200 text-xs font-semibold uppercase tracking-wider text-red-600 hover:bg-red-50 hover:border-red-200 transition-colors"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        <AddZoneModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSave={saveZone}
          initial={editing}
          onCreateHall={createHall}
        />

        <AddHallModal
          open={hallModalOpen}
          onClose={() => {
            setHallModalOpen(false);
            setEditingHall(null);
          }}
          onSave={handleSaveHall}
          initial={editingHall}
        />

        <ConfirmDelete
          open={delOpen}
          onClose={() => setDelOpen(false)}
          onConfirm={doDelete}
          title="Delete Zone?"
          message={`This will permanently remove "${toDelete?.name ?? ""}" from your event.`}
        />

        <ConfirmDelete
          open={delHallOpen}
          onClose={() => setDelHallOpen(false)}
          onConfirm={doDeleteHall}
          title="Delete Hall?"
          message={`This will remove hall "${toDeleteHall?.name ?? ""}". Any zones assigned to this hall will be unassigned.`}
        />

        {zoneSettingsModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 px-4 py-6">
            <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl overflow-hidden flex flex-col border border-gray-100">
              <div className="shrink-0 flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    {zoneSettingsModal.title}
                  </h2>
                  {zoneSettingsModal.subtitle ? (
                    <p className="mt-0.5 text-xs text-gray-500">{zoneSettingsModal.subtitle}</p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => setZoneSettingsModal(null)}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
                >
                  <FaTimes className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 space-y-4">
                <div className="space-y-4">
                  <div className="rounded-xl border border-gray-200 bg-white p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between shadow-xs">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">Multiple Entry</h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Allow attendees to check in multiple times.
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span>Off</span>
                      <button
                        type="button"
                        onClick={() =>
                          setZoneSettingsModal((prev) => ({
                            ...prev,
                            multipleEntry: !prev.multipleEntry,
                            requiredCheckOut: prev.multipleEntry ? false : prev.requiredCheckOut,
                          }))
                        }
                        className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors ${
                          zoneSettingsModal.multipleEntry ? "bg-blue-600" : "bg-gray-300"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            zoneSettingsModal.multipleEntry ? "translate-x-7" : "translate-x-1"
                          }`}
                        />
                      </button>
                      <span className="font-semibold text-gray-800">On</span>
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-white p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between shadow-xs">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">Required Check Out</h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Require check-out before the next check-in.
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span>Off</span>
                      <button
                        type="button"
                        disabled={!zoneSettingsModal.multipleEntry}
                        onClick={() =>
                          setZoneSettingsModal((prev) => ({
                            ...prev,
                            requiredCheckOut: !prev.requiredCheckOut,
                          }))
                        }
                        className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors ${
                          zoneSettingsModal.requiredCheckOut && zoneSettingsModal.multipleEntry
                            ? "bg-blue-600"
                            : "bg-gray-300"
                        } ${!zoneSettingsModal.multipleEntry ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            zoneSettingsModal.requiredCheckOut && zoneSettingsModal.multipleEntry
                              ? "translate-x-7"
                              : "translate-x-1"
                          }`}
                        />
                      </button>
                      <span className="font-semibold text-gray-800">On</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="shrink-0 flex flex-col sm:flex-row sm:justify-end gap-3 border-t border-gray-100 px-5 py-4 bg-gray-50/50">
                <button
                  type="button"
                  onClick={() => setZoneSettingsModal(null)}
                  className="h-9 px-4 rounded-md border border-gray-200 text-xs font-semibold uppercase tracking-wider text-gray-700 hover:bg-white"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveZoneSettings}
                  className="h-9 px-5 rounded-md bg-[#123047] text-white text-xs font-semibold uppercase tracking-wider hover:bg-[#0b2235] shadow-xs"
                >
                  Apply Settings
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {activeMenu &&
        typeof document !== "undefined" &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[999]"
              onClick={(e) => {
                e.stopPropagation();
                setActiveMenu(null);
              }}
            />
            <div
              className="fixed z-[1000] w-44 rounded-lg border border-gray-200 bg-white py-1 text-sm shadow-xl"
              style={{ top: menuPos.top, left: menuPos.left }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="flex items-center gap-2 w-full px-3.5 py-2 text-left text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                onClick={() => {
                  const target = activeMenu;
                  setActiveMenu(null);
                  openZoneSettings(target);
                }}
              >
                <Sliders className="w-3.5 h-3.5 text-gray-400" />
                <span>Settings</span>
              </button>
              <button
                type="button"
                className="flex items-center gap-2 w-full px-3.5 py-2 text-left text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                onClick={() => {
                  const target = activeMenu;
                  setActiveMenu(null);
                  openAssignee(target);
                }}
              >
                <Users className="w-3.5 h-3.5 text-gray-400" />
                <span>Assignee</span>
              </button>
              <button
                type="button"
                className="flex items-center gap-2 w-full px-3.5 py-2 text-left text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                onClick={() => {
                  const target = activeMenu;
                  setActiveMenu(null);
                  openEdit(target);
                }}
              >
                <Edit2 className="w-3.5 h-3.5 text-gray-400" />
                <span>Edit Details</span>
              </button>
              <div className="my-1 border-t border-gray-100" />
              <button
                type="button"
                className="flex items-center gap-2 w-full px-3.5 py-2 text-left text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
                onClick={() => {
                  const target = activeMenu;
                  setActiveMenu(null);
                  setToDelete(target);
                  setDelOpen(true);
                }}
              >
                <Trash2 className="w-3.5 h-3.5 text-red-500" />
                <span>Delete Zone</span>
              </button>
            </div>
          </>,
          document.body
        )}

      <BulkAssigneeImportModal
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        entityType="Zone"
        eventId={eventId}
        token={token}
        entities={zones}
        onImport={handleBulkImportZoneAssignees}
      />
    </div>
  );
}

export { AddZoneModal as ZoneModal };
export default ZonesPage;
